"use client"

import * as React from "react"
import { FolderOpen, Plus, Settings, Trash2, X, Pencil, AlertTriangle, Loader2 } from "lucide-react"
import { TabsContent } from "@/components/ui/tabs"
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Account, UserProfile, Workspace } from "@/types/workspace"
import { WorkspaceMembersManager } from "@/components/settings/tabs/WorkspaceMembersManager"
import { toast } from "@/lib/toast"

export interface WorkspacesSettingsTabProps {
  currentAccount: Account | null
  handleCreateWorkspace: (name: string, accountId: string) => Promise<any>
  handleUpdateWorkspace?: (id: string, name: string) => Promise<any>
  handleDeleteWorkspace?: (id: string) => Promise<any>
  usersList: UserProfile[]
  t: Record<string, string>
  profileRole?: string
  user?: UserProfile | null
}

export function WorkspacesSettingsTab({
  currentAccount,
  handleCreateWorkspace,
  handleUpdateWorkspace,
  handleDeleteWorkspace,
  usersList,
  t,
  profileRole = "member",
  user,
}: WorkspacesSettingsTabProps) {
  const isCompanyAdmin = profileRole === "owner" || profileRole === "admin"
  const [newWsName, setNewWsName] = React.useState("")
  const [showCreateWorkspaceModal, setShowCreateWorkspaceModal] = React.useState(false)
  const [isSubmittingWorkspace, setIsSubmittingWorkspace] = React.useState(false)
  const [editingWorkspaceModal, setEditingWorkspaceModal] = React.useState<any | null>(null)
  const [editWorkspaceNameInput, setEditWorkspaceNameInput] = React.useState("")
  const [userWorkspaceRoles, setUserWorkspaceRoles] = React.useState<Record<string, string>>({})
  const [workspaceToDelete, setWorkspaceToDelete] = React.useState<Workspace | null>(null)
  const [isDeletingWorkspace, setIsDeletingWorkspace] = React.useState(false)

  // Buscar papéis do usuário nos workspaces caso ele seja membro comum da empresa
  React.useEffect(() => {
    if (isCompanyAdmin) return
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("token="))
      ?.split("=")[1]
    const userId = user?.id || (user as any)?.userId
    if (!token || !userId) return

    fetch("/api/graphql", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        query: `
          query GetUserWorkspaceMemberships($userId: ID!) {
            userWorkspaceMemberships(userId: $userId) {
              workspaceId
              role
            }
          }
        `,
        variables: { userId }
      })
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.data?.userWorkspaceMemberships) {
          const map: Record<string, string> = {}
          res.data.userWorkspaceMemberships.forEach((m: any) => {
            map[m.workspaceId] = m.role
          })
          setUserWorkspaceRoles(map)
        }
      })
      .catch((e) => console.error("Erro ao buscar papéis nos workspaces:", e))
  }, [user?.id, (user as any)?.userId, isCompanyAdmin])

  return (
    <TabsContent value="workspaces" className="outline-none space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-zinc-900 dark:text-white flex items-center gap-2">
            <FolderOpen className="size-5 text-[#004EB1]" />
            Gerenciar Workspaces
          </h3>
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 font-sans">
            {isCompanyAdmin
              ? "Crie e gerencie os espaços de trabalho da sua empresa, configure membros e permissões."
              : "Visualize e gerencie os espaços de trabalho aos quais você tem acesso nesta empresa."}
          </p>
        </div>

        {isCompanyAdmin && (
          <Button
            type="button"
            onClick={() => {
              setNewWsName("")
              setShowCreateWorkspaceModal(true)
            }}
            className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold h-9 px-4 gap-1.5 cursor-pointer shadow-sm"
          >
            <Plus className="size-4" />
            Criar Workspace
          </Button>
        )}
      </div>

      {/* Lista de Workspaces da Conta em Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(currentAccount?.workspaces || []).map((ws) => {
          const isWorkspaceAdmin = isCompanyAdmin || userWorkspaceRoles[ws.id] === "admin"

          return (
            <Card key={ws.id} className="border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:border-zinc-300 dark:hover:border-zinc-700 transition-all shadow-xs">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="size-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-[#004EB1] dark:text-blue-400 border border-blue-100 dark:border-blue-900/50 flex items-center justify-center font-bold text-base">
                      {ws.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <CardTitle className="text-sm font-bold text-zinc-900 dark:text-white">
                        {ws.name}
                      </CardTitle>
                      <CardDescription className="text-[11px] text-zinc-400 font-mono mt-0.5">
                        {ws.slug}
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center justify-between pt-3 border-t border-zinc-100 dark:border-zinc-800 mt-2">
                  <div className="text-[11px] text-zinc-400 font-medium">
                    Workspace Corporativo
                  </div>
                  {isWorkspaceAdmin ? (
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setEditingWorkspaceModal(ws)
                          setEditWorkspaceNameInput(ws.name)
                        }}
                        className="h-8 text-xs font-semibold text-[#004EB1] border-blue-200 dark:border-blue-900 hover:bg-blue-50 dark:hover:bg-blue-950/30 gap-1.5 cursor-pointer"
                      >
                        <Settings className="size-3.5" />
                        Configurar
                      </Button>

                      {isCompanyAdmin && handleDeleteWorkspace && (currentAccount?.workspaces?.length || 0) > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setWorkspaceToDelete(ws)}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 cursor-pointer"
                          title="Excluir Workspace"
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      )}
                    </div>
                  ) : (
                    <span className="text-[10px] font-semibold text-zinc-500 dark:text-zinc-400 bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 rounded-md">
                      Acesso Concedido
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* MODAL DE CRIAÇÃO DE WORKSPACE */}
      {showCreateWorkspaceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
          <div className="w-full max-w-md bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-2xl overflow-hidden font-sans animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50">
              <div className="flex items-center gap-2">
                <div className="size-8 rounded-lg bg-blue-50 dark:bg-blue-950/50 text-[#004EB1] flex items-center justify-center">
                  <Plus className="size-4" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-zinc-900 dark:text-white">Criar Novo Workspace</h4>
                  <p className="text-[11px] text-zinc-500">Informe o nome do seu novo espaço de trabalho</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateWorkspaceModal(false)}
                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault()
                if (!newWsName.trim() || !currentAccount) return
                setIsSubmittingWorkspace(true)
                try {
                  const newWs = await handleCreateWorkspace(newWsName.trim(), currentAccount.id)
                  if (currentAccount) {
                    currentAccount.workspaces = [...(currentAccount.workspaces || []), newWs]
                  }
                  toast.success(`Workspace "${newWs.name}" criado com sucesso!`)
                  setNewWsName("")
                  setShowCreateWorkspaceModal(false)
                } catch (err: any) {
                  toast.error(err.message || "Erro ao criar workspace.")
                } finally {
                  setIsSubmittingWorkspace(false)
                }
              }}
              className="p-6 space-y-4"
            >
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-zinc-700 dark:text-zinc-300">
                  Nome do Workspace <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={newWsName}
                  onChange={(e) => setNewWsName(e.target.value)}
                  placeholder="Ex: Vendas, Marketing, Filial SP"
                  className="w-full rounded-lg border border-zinc-200 dark:border-zinc-800 px-3.5 py-2 text-xs bg-white dark:bg-zinc-950 text-zinc-900 dark:text-white focus:border-[#004EB1] focus:ring-1 focus:ring-[#004EB1] outline-none"
                />
                <p className="text-[10px] text-zinc-400">
                  O identificador slug será gerado automaticamente a partir do nome.
                </p>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100 dark:border-zinc-800">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setShowCreateWorkspaceModal(false)}
                  className="text-xs h-9 px-4 cursor-pointer"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={!newWsName.trim() || isSubmittingWorkspace}
                  className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs font-semibold h-9 px-5 cursor-pointer"
                >
                  {isSubmittingWorkspace ? "Criando..." : "Criar Workspace"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL DE CONFIGURAÇÃO / EDIÇÃO DO WORKSPACE */}
      {editingWorkspaceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 overflow-y-auto">
          <div className="w-full max-w-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl shadow-2xl overflow-hidden font-sans my-8 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50">
              <div className="flex items-center gap-3">
                <div className="size-9 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-[#004EB1] flex items-center justify-center font-bold">
                  {editingWorkspaceModal.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h4 className="text-sm font-bold text-zinc-900 dark:text-white">
                    Configurar Workspace
                  </h4>
                  <p className="text-[11px] text-zinc-500 font-mono">
                    {editingWorkspaceModal.slug}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditingWorkspaceModal(null)}
                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
              {/* Seção 1: Alterar Nome */}
              <div className="p-4 bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-xl space-y-3">
                <h5 className="text-xs font-bold text-zinc-900 dark:text-white uppercase tracking-wider flex items-center gap-2">
                  <Pencil className="size-3.5 text-[#004EB1]" />
                  Nome de Exibição
                </h5>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={editWorkspaceNameInput}
                    onChange={(e) => setEditWorkspaceNameInput(e.target.value)}
                    placeholder="Nome do Workspace"
                    className="flex-1 rounded-lg border border-zinc-200 dark:border-zinc-800 px-3.5 py-2 text-xs bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white focus:border-[#004EB1] outline-none"
                  />
                  <Button
                    type="button"
                    disabled={!editWorkspaceNameInput.trim() || editWorkspaceNameInput === editingWorkspaceModal.name}
                    onClick={async () => {
                      if (!handleUpdateWorkspace || !editingWorkspaceModal) return
                      try {
                        const updated = await handleUpdateWorkspace(editingWorkspaceModal.id, editWorkspaceNameInput.trim())
                        if (currentAccount) {
                          currentAccount.workspaces = (currentAccount.workspaces || []).map((w) =>
                            w.id === updated.id ? { ...w, name: updated.name } : w
                          )
                        }
                        setEditingWorkspaceModal({ ...editingWorkspaceModal, name: updated.name })
                        toast.success("Nome do workspace atualizado!")
                      } catch (err: any) {
                        toast.error(err.message || "Erro ao atualizar nome.")
                      }
                    }}
                    className="bg-[#004EB1] hover:bg-[#003d8d] text-white text-xs h-9 px-4 cursor-pointer"
                  >
                    Salvar
                  </Button>
                </div>
              </div>

              {/* Seção 2: Membros e Perfis do Workspace */}
              <div className="space-y-3">
                <WorkspaceMembersManager
                  workspaceId={editingWorkspaceModal.id}
                  workspaceName={editingWorkspaceModal.name}
                  allUsers={usersList}
                />
              </div>
            </div>

            <div className="px-6 py-3 border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/50 flex justify-end">
              <Button
                type="button"
                onClick={() => setEditingWorkspaceModal(null)}
                className="bg-zinc-800 hover:bg-zinc-700 text-white text-xs font-semibold h-8 px-4 cursor-pointer"
              >
                Concluir
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO DE WORKSPACE */}
      {workspaceToDelete && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[999] animate-in fade-in duration-200 p-4 font-sans">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 p-5 space-y-4">
            <div className="size-11 rounded-full bg-red-100 dark:bg-red-950/50 text-red-600 dark:text-red-400 flex items-center justify-center mx-auto shadow-xs">
              <AlertTriangle className="size-5" />
            </div>

            <div className="text-center space-y-1.5">
              <h3 className="text-sm font-bold text-zinc-900 dark:text-white">
                Excluir Workspace
              </h3>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
                Tem certeza de que deseja excluir o workspace <strong>{workspaceToDelete.name}</strong>?
                <br />
                Esta ação é irreversível e todos os dados, permissões e configurações associadas a este workspace serão excluídos.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-150 dark:border-zinc-800">
              <Button
                type="button"
                variant="ghost"
                disabled={isDeletingWorkspace}
                onClick={() => setWorkspaceToDelete(null)}
                className="text-xs h-8 font-sans cursor-pointer flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="button"
                disabled={isDeletingWorkspace}
                onClick={async () => {
                  if (!handleDeleteWorkspace || !workspaceToDelete) return
                  setIsDeletingWorkspace(true)
                  try {
                    await handleDeleteWorkspace(workspaceToDelete.id)
                    toast.success(`Workspace "${workspaceToDelete.name}" removido com sucesso.`)
                    setWorkspaceToDelete(null)
                  } catch (err: any) {
                    toast.error(err.message || "Erro ao excluir workspace.")
                    setIsDeletingWorkspace(false)
                  }
                }}
                className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold h-8 px-4 gap-1.5 cursor-pointer flex-1 shadow-xs"
              >
                {isDeletingWorkspace ? (
                  <>
                    <Loader2 className="size-3.5 animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  "Excluir"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </TabsContent>
  )
}
